import React, { useState, useEffect,useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import AppHeader from '../../components/AppHeader';
import AppSidebar from '../../components/AppSidebar';
import TasksService from '../../services/TasksService';
import TaskCounts from './TaskComponents/TaskCounts';
import UsersService from '../../services/UsersService';
import { toast, ToastContainer } from 'react-toastify';
import { useForm } from 'react-hook-form';
import Assign from '../../modals/Assign';
import ChangeStatusModal from '../../modals/ChangeStatus';
import CommonTable from '../../components/CommonTable';
import Pagination from '../../components/Pagination';
import Filters from '../../components/Filters';
import UseDebounce from '../../hooks/UseDebounce';
import { PageLimit } from '../../utils/ConstantUtils';
import { DebounceDelay } from '../../utils/ConstantUtils';
import { taskStatusOptions } from '../../utils/OptionUtils';

const Tasks = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const pageLimit = PageLimit;
  const [itemsPerPage, setPageLimit] = useState(pageLimit);
  const offset = (currentPage - 1) * itemsPerPage;
  const [appliedFilters, setAppliedFilters] = useState({});
  const debouncedSearchTerm = UseDebounce(searchTerm, DebounceDelay || 100)
  const [showStatusModal, setShowStatusModal] = useState(false);
  const { control: statusControl, handleSubmit: handleStatusSubmit } = useForm();
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  
  const columns = [
    { Header: '', accessor: '' },
    { Header: '#', accessor: 'id' },
    { Header: 'Subject', accessor: 'subject' },
    { Header: 'Start Date', accessor: 'start_date' },
    { Header: 'Due Date', accessor: 'due_date' },
    { Header: 'Description', accessor: 'description' },
    { Header: 'Priority', accessor: 'priority' },
    { Header: 'Assignees', accessor: 'assignees' },
    { Header: 'Status', accessor: 'status' },
    { Header: 'Type', accessor: 'type' },
    { Header: 'Tags', accessor: 'tags' },
    { Header: 'Created By', accessor: 'createdBy' },

  ];
  const [selectedRows, setSelectedRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [AssigneeList, setAssigneeList] = useState([]);
  const { formState: { errors }, control, handleSubmit } = useForm();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskCounts, setTaskCounts] = useState({
    not_started: 0,
    in_progress: 0,
    completed: 0,
    on_hold: 0,
    canceled: 0,
    awaiting_feedback: 0,
    testing: 0,
  })

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await TasksService.getAllTasks({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm,
        offset: offset,
        filters: appliedFilters,
      });
      if (response.status === 200 && response.data.success) {
        setRows(response.data.data.task || []);
        setTaskCounts(response?.data?.data?.counts);
        setTotalPages(response?.data?.data?.totalPages);
        setCurrentPage(response?.data?.data?.currentPage);
        await fetchAssignees();
      } else {
        console.error('Error in fetching Tasks data:', response);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error(error?.response?.data?.message || error?.message);

    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, offset, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [appliedFilters,fetchData]);

  const fetchAssignees = async () => {
    try {
      const response = await UsersService.list();
      const formattedAssigneeList = response.data.data.map(assignee => ({
        value: assignee.id,
        label: assignee.name + ' ' + assignee.email,
      }));
      setAssigneeList(formattedAssigneeList);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      console.error('Failed to load assignees');
    }
  };

  const onSubmit = async (data) => {
    const assigneeIds = data.assignee.map((assignee) => assignee.value);
    const requestBody = {
      data: {
        assignee: assigneeIds,
      },
    };

    try {
      const taskId = selectedRows[0];
      const response = await TasksService.taskAssignToUser(taskId, requestBody);

      if (response.status === 200) {
        const taskResponse = await TasksService.getTask(taskId);
        if (taskResponse.status) {
          updateTaskInRows(taskResponse.data.data);
        } else {
          toast.error(taskResponse?.data?.message);
        }

        toast.success('Task assigned to user successfully');
        handleCloseAssignModal();
      } else {
        toast.error('Failed to assign task');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const onSubmitStatus = async (data) => {
    try {
      const requestBody = {
        data: {
          status: data.status ? data.status.value : null
        }
      };

      const response = await TasksService.changeTaskStatus(selectedTaskId, requestBody);
      if (response.status === 200) {
        setRows(prevRows => prevRows.map(row =>
          row.id === selectedTaskId ? { ...row, status: data.status.value } : row
        ));
        toast.success('Task status updated successfully');
        handleCloseStatusModal();
      } else {
        toast.error('Failed to update task status');
      }
    } catch (error) {
      toast.error('An error occurred while updating the task status');
    }
  };

  const handleShowStatusModal = (taskId) => {
    setSelectedTaskId(taskId);
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setSelectedTaskId(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'not_started':
        return 'badge bg-primary';
      case 'in_progress':
        return 'badge bg-warning';
      case 'completed':
        return 'badge bg-success';
      case 'on_hold':
        return 'badge bg-danger';
      case 'testing':
        return 'badge bg-secondary';
      default:
        return 'badge bg-info';
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setCurrentPage(1);
    setPageLimit(e.target.value);
  };

  const handleFilterSubmit = (newFilters) => {
    setAppliedFilters(newFilters);
  };

  const handleShowAssignModal = (taskId) => {
    setSelectedRows([taskId]);
    setShowAssignModal(true);
  }
  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedRows([]);
  }

  const updateTaskInRows = (updatedTask) => {
    setRows(prevRows =>
      prevRows.map(row => (row.id === updatedTask.id ? updatedTask : row))
    );
  };

  const filterFields = [
    { name: 'subject', placeholder: 'Subject', Dropdown : "yes" },
    { name: 'priority', placeholder: 'Priority', Dropdown : "yes" },
    { name: 'type', placeholder: 'Type', Dropdown : "yes" },
    { name: 'status', placeholder: 'Status', Dropdown : "yes" },
    { name: 'start_date', placeholder: 'Start Date', Dropdown : "no" },
    { name: 'due_date', placeholder: 'Due Date', Dropdown : "no" },
    { name: 'created_by', placeholder: 'Created By', Dropdown : "yes" },
  ];

  return (
    <div>
      <AppSidebar />
      <div className="main-content">
        <AppHeader />
        <div className="container-fluid">
          <div className='row justify-content-center'>
            <div className="col-12">
              <PageHeader title="Task Summary" button_label="Create" link_to="create" className="btn btn-sm btn-primary" />
              <TaskCounts taskCounts={taskCounts} />
              <div className="tab-content">
                <div className="tab-pane fade show active" id="contactsListPane" role="tabpanel" aria-labelledby="contactsListTab">
                  <div className="card">
                    <div className="card-header">
                      <div className="row align-items-center">
                        <div className="col">
                          <form>
                            <div className="input-group input-group-flush input-group-merge input-group-reverse">
                              <input
                                className="form-control list-search"
                                type="search"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleSearchChange}
                              />
                              <span className="input-group-text">
                                <i className="fe fe-search" />
                              </span>
                            </div>
                          </form>
                        </div>
                        <div className="col-auto me-n3">
                          <form>
                            <select
                              className="form-select form-select-sm form-control-flush"
                              value={itemsPerPage}
                              onChange={handleItemsPerPageChange}
                            >
                              <option value="20">20 per page</option>
                              <option value="40">40 per page</option>
                              <option value="60">60 per page</option>
                              <option value="100">100 per page</option>
                            </select>
                          </form>
                        </div>
                        <div className="col-auto">
                          {/* Use the Filters component here */}
                          <Filters filterFields={filterFields} onApplyFilters={handleFilterSubmit} getFilterService={TasksService.getTasksFilter} />
                        </div>
                      </div>
                    </div>
                    {/* Common Table component*/}
                    <CommonTable
                      loading={loading}
                      rows={rows}
                      columns={columns}
                      selectedRows={selectedRows}
                      handleShowAssignModal={handleShowAssignModal}
                      setSelectedRows={setSelectedRows}
                      basePath="tasks"
                      handleShowStatusModal={handleShowStatusModal}
                      getStatusBadgeClass={getStatusBadgeClass}
                      offset = {offset}
                      showCheckboxes = {false}
                    />
                    <ToastContainer />
                    
                    {/* Pagination Component */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      setCurrentPage={setCurrentPage}
                    />
                    
                    {/* Assign Task Modal */}
                    <Assign
                      show={showAssignModal}
                      handleClose={handleCloseAssignModal}
                      handleSubmit={handleSubmit}
                      control={control}
                      AssigneeList={AssigneeList}
                      onSubmit={onSubmit}
                      errors={errors}
                    />

                    {/* Change Status Modal */}
                    <ChangeStatusModal
                      show={showStatusModal}
                      handleClose={handleCloseStatusModal}
                      handleSubmit={handleStatusSubmit}
                      control={statusControl}
                      status={taskStatusOptions}
                      onSubmit={onSubmitStatus}
                      errors={errors}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;